/**
 * Runs submitted tasks with at most `limit` in flight, in submission order. A task that throws
 * frees its slot like one that succeeds, so one failure never stalls the queue. The caller owns
 * cancellation: a task that should no longer run checks its own AbortSignal when it starts.
 */
export function createTaskPool(limit: number) {
  if (!Number.isInteger(limit) || limit < 1) throw new Error("Task pool limit must be a positive integer");
  let active = 0;
  const waiting: Array<() => void> = [];

  const next = () => {
    if (active >= limit) return;
    const start = waiting.shift();
    if (!start) return;
    active++;
    start();
  };

  return {
    run<T>(task: () => Promise<T>): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        waiting.push(() => {
          // Deferred through a promise so a synchronous throw also lands in `reject`.
          Promise.resolve()
            .then(task)
            .then(resolve, reject)
            .finally(() => {
              active--;
              next();
            });
        });
        next();
      });
    },
  };
}
